import * as React from 'react'
import * as TogglePrimitive from '@radix-ui/react-toggle'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/lib/utils'

const toggleVariants = cva(
  cn(
    'inline-flex items-center justify-center gap-2 rounded-md outline-none transition-[color,box-shadow]',
    'text-sm font-semibold cursor-pointer whitespace-nowrap',
    'hover:bg-neutral-100 hover:text-neutral-500',
    'disabled:pointer-events-none disabled:opacity-50',
    'focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]',
    'data-[state=on]:bg-secondary data-[state=on]:text-neutral-50 data-[state=on]:font-bold',
    'aria-invalid:ring-destructive/20 aria-invalid:border-destructive',
    "[&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 [&_svg]:shrink-0"
  ),
  {
    variants: {
      variant: {
        default: 'bg-transparent',
        outline:
          'border border-gray-200 bg-transparent shadow-xs hover:bg-neutral-100 hover:text-neutral-900',
        white:
          'border-y border-l last:border-r border-gray-200 bg-white shadow-xs hover:bg-neutral-100 hover:text-neutral-900',
      },
      size: {
        default: 'h-9 px-2 min-w-9',
        sm: 'h-8 px-1.5 min-w-8',
        md: 'h-10 px-3 min-w-10',
        lg: 'h-15.5 px-8 min-w-15.5 data-[spacing=0]:first:rounded-l-xl data-[spacing=0]:last:rounded-r-xl [&_i]:text-xl',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
)

function Toggle({
  className,
  variant,
  size,
  ...props
}: React.ComponentProps<typeof TogglePrimitive.Root> &
  VariantProps<typeof toggleVariants>) {
  return (
    <TogglePrimitive.Root
      data-slot="toggle"
      className={cn(toggleVariants({ variant, size, className }))}
      {...props}
    />
  )
}

export { Toggle, toggleVariants }
